import { useLocation } from 'react-router-dom';
import { breakpoints, Logo, useWindowSize } from '@bphxd/ds-core-react';
import { Down24, Support24 } from '@bphxd/ds-core-react/lib/icons';
import { useState } from 'react';
import { Navbar, NavbarBrand, Nav, NavItem, NavLink, Offcanvas } from 'reactstrap';
import { FCC } from '../../lib/types';
import AssetSelection from '../organisms/AssetSelection/AssetSelection';
import ProfileAvatar from '../organisms/ProfileAvatar/ProfileAvatar';
import { useParsedSearchParams } from '../../lib/util';
import Footer from './Footer';
import SubtrackerNavbar from '../organisms/SubtrackerNavbar/SubtrackerNavbar';
import classes from './Layout.module.scss';
import { useQuery } from '@apollo/client';
import { GET_CONFIG, GET_SUPPORT_TEAM_EMAILS } from '../../graph-queries/common/config';
import NoticeBarProvider from '../molecules/NoticeBar/NoticeBar';
import { TARS_AND_OUTAGES_PATH, MAINTANENCE_PATH } from '../../constants';

const tarsAndOutagesSubtrackerItems = [
  {
    label: 'Events',
    link: `/${TARS_AND_OUTAGES_PATH}/events`,
  },
  {
    label: 'Workpacks',
    link: `/${TARS_AND_OUTAGES_PATH}/workpacks`,
  },
  {
    label: 'Scaffolds',
    link: `/${TARS_AND_OUTAGES_PATH}/scaffolds`,
  },
  {
    label: 'Insulations',
    link: `/${TARS_AND_OUTAGES_PATH}/insulations`,
  },
  {
    label: 'Isolations',
    link: `/${TARS_AND_OUTAGES_PATH}/isolations`,
  },
  {
    label: 'MOCs',
    link: `/${TARS_AND_OUTAGES_PATH}/emocs`,
  },
  {
    label: 'Lift Plans',
    link: `/${TARS_AND_OUTAGES_PATH}/liftplans`,
  },
  {
    label: 'Leak Test Packs',
    link: `/${TARS_AND_OUTAGES_PATH}/leaktestpacks`,
  },
  {
    label: 'Procedures',
    link: `/${TARS_AND_OUTAGES_PATH}/procedures`,
  },
  {
    label: 'Purchase Orders',
    link: `/${TARS_AND_OUTAGES_PATH}/purchaseorders`,
  },
  {
    label: 'Permits',
    link: `/${TARS_AND_OUTAGES_PATH}/permits`,
  },
];

const maintenanceSubtrackerItems = [
  {
    label: 'Manage Certificates',
    link: `/${MAINTANENCE_PATH}/certificates`,
  },
  // {
  //   label: 'Execute Certificates',
  //   link: `/${MAINTANENCE_PATH}/certificates/search`,
  // },
  {
    label: 'Monitor Certificates',
    link: `/${MAINTANENCE_PATH}/certificates/monitoring`,
  },
];

const launchTeams = (supportTeamEmails: string) => {
  const chatUrl = `https://teams.microsoft.com/l/chat/0/0?users=${encodeURIComponent(supportTeamEmails)}`;
  return window.open(chatUrl, '_blank');
};

const Layout: FCC = ({ children }) => {
  const location = useLocation();

  const [asset] = useParsedSearchParams('asset');
  const [isSearchOpen, setSearchIsOpen] = useState(false);
  const breakpoint = 'md';
  const { width } = useWindowSize();
  const { data: config } = useQuery(GET_CONFIG);
  const { data: users } = useQuery(GET_SUPPORT_TEAM_EMAILS);
  const isHomePage = location.pathname === '/' ? 1 : 0;
  const toggleSearch = () => {
    setSearchIsOpen(!isSearchOpen);
  };

  const isFlocSearchEnabled = !!config?.config.featureFlags.find(
    (ff) => ff.flag === 'FEATURE_FLAG_ENABLE_FLOC_SEARCH'
  )?.isEnabled;

  const renderAssetSelection = <AssetSelection />;

  return (
    <>
      <Navbar expand className="navbar-border" sticky="top">
        <div className="d-flex align-items-center">
          <NavbarBrand href={`/`}>
            <Logo className="mx-n3" />
            <span className="ms-5">Digital Twin Companion App</span>
          </NavbarBrand>
        </div>

        {/* right side */}
        <Nav navbar className={`flex-grow-1 me-4`}>
          <NavItem
            className={`d-none d-${breakpoint}-flex flex-grow-1 me-4 justify-content-center align-items-center`}
          >
            <div className="d-flex text-center">
                {isFlocSearchEnabled && (
                  <NavLink
                    className={`me-4 ${location.pathname === '/' ? classes.homePageTracker : location.pathname.includes('/materials') ? classes.selectedTracker : classes.nonSelectedTracker}`}
                    href={`/materials?asset=${asset}`}
                    role="button"
                    disabled={!asset}
                    title={!asset ? 'Select Asset from dropdown' : 'Go to Media'}
                  >
                    Media
                  </NavLink>
                )}

                <NavLink
                  className={`ms-4 ${location.pathname === '/' ? classes.homePageTracker : location.pathname.includes(TARS_AND_OUTAGES_PATH) ? classes.selectedTracker : classes.nonSelectedTracker}`}
                  href={`/${TARS_AND_OUTAGES_PATH}/events?asset=${asset}`}
                  id="tar-outages-nav-item"
                  role="button"
                  disabled={!asset}
                  title={!asset ? 'Select Asset from dropdown' : 'Go to TARs & Outages'}
                >
                  TARs & Outages
                </NavLink>

                <NavLink
                  className={`ms-4 ${location.pathname === '/' ? classes.homePageTracker : location.pathname.includes(MAINTANENCE_PATH) ? classes.selectedTracker : classes.nonSelectedTracker}`}
                  href={`/${MAINTANENCE_PATH}/certificates?asset=${asset}`}
                  disabled={!asset}
                  id="maintenacne-nav-item"
                  role="button"
                  title={!asset ? 'Select Asset from dropdown' : 'Go to Maintenance'}
                >
                  Maintenance
                </NavLink>
              </div>
          </NavItem>
          <NavItem className={`d-none d-${breakpoint}-flex me-4`}>{renderAssetSelection}</NavItem>
          <NavItem className={`d-${breakpoint}-none`}>
            <NavLink href="#" onClick={toggleSearch} active={isSearchOpen}>
              <span>
                <Down24 />
              </span>
            </NavLink>
          </NavItem>
          <NavItem>
            <NavLink style={{ cursor: 'pointer' }}>
              <span>
                <Support24 onClick={() => launchTeams(users?.supportTeamEmails || '')} />
              </span>
            </NavLink>
          </NavItem>
        </Nav>
        <ProfileAvatar />
      </Navbar>

      {width < breakpoints[breakpoint] && (
        <>
          <Offcanvas
            direction="top"
            isOpen={isSearchOpen}
            toggle={toggleSearch}
            className="offcanvas-sidebar pt-11"
            backdropClassName="offcanvas-backdrop-sidebar"
            style={{ height: '150px' }}
            zIndex="auto"
          >
            <div className="px-4 py-3">{renderAssetSelection}</div>
          </Offcanvas>
        </>
      )}

      {location.pathname.includes(TARS_AND_OUTAGES_PATH) && !isHomePage && (
        <SubtrackerNavbar subtrackerItems={tarsAndOutagesSubtrackerItems} />
      )}

      {location.pathname.includes(MAINTANENCE_PATH) && !isHomePage && (
        <SubtrackerNavbar subtrackerItems={maintenanceSubtrackerItems} />
      )}

      <div className="d-flex h-100">
        <section className="flex-grow-1 bg-light-gray-200">
          <NoticeBarProvider>{children}</NoticeBarProvider>
        </section>
      </div>
      <Footer />
    </>
  );
};

export default Layout;
